import { useState, useMemo, useCallback } from "react";
import { FiImage, FiSearch } from "react-icons/fi";
import { InputGroup, InputLeftElement, Input } from "@chakra-ui/react";
import type { RouteComponentProps } from "@reach/router";

import { SectorSelect } from "newui/components/ui/CommonSelects";
import SkeletonBase from "newui/components/Skeleton";

import { useCompanyPreviewData } from "../hooks/useCompanyPreviewData";
import { useProjectsFilter } from "../hooks/useProjectsFilter";
import { ProjectCard } from "./ProjectCard";
import { ProjectDetailModal } from "./ProjectDetailModal";
import { MobileProjectRow } from "./mobile/MobileProjectRow";
import { MobilePrimaryProjectCard } from "./mobile/MobilePrimaryProjectCard";
import { PROFILE_TYPE } from "../types";
import type { PastProject } from "newui/features/Company/types";
import type { SelectOption } from "newui/components/ui/InputWithLabel/SelectWithLabel";
import FallbackImage from "newui/components/FallbackImage/FallbackImage";

interface ProjectsExperienceTabProps extends RouteComponentProps {
	companyId?: number;
	path?: string;
}

interface SortState {
	direction: "asc" | "desc";
	field: string;
}

export const ProjectsExperienceTab: React.FC<ProjectsExperienceTabProps> = ({
	companyId = 0,
}) => {
	const { company, pastProjects, profileTypeId } = useCompanyPreviewData();
	const { filteredProjects, isFiltering, filterProjects, clearFilter } =
		useProjectsFilter(companyId);

	const [searchTerm, setSearchTerm] = useState("");
	const [sectorId, setSectorId] = useState<number | null>(null);
	const [selectedProject, setSelectedProject] = useState<PastProject | null>(
		null
	);
	const [sort, setSort] = useState<SortState>({ field: "", direction: "asc" });

	const projects = filteredProjects ?? pastProjects;
	const isSubcontractor = profileTypeId === PROFILE_TYPE.TRADE;

	const handleSearch = useCallback(() => {
		if (searchTerm || sectorId) {
			filterProjects(searchTerm, sectorId);
		} else {
			clearFilter();
		}
	}, [searchTerm, sectorId, filterProjects, clearFilter]);

	const handleSearchKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === "Enter") handleSearch();
		},
		[handleSearch]
	);

	const handleSectorChange = useCallback(
		(option: SelectOption | SelectOption[] | null) => {
			const selected = Array.isArray(option) ? option[0] : option;
			const newSectorId = selected?.value ? Number(selected.value) : null;
			setSectorId(newSectorId);
			if (searchTerm || newSectorId) {
				filterProjects(searchTerm, newSectorId);
			} else {
				clearFilter();
			}
		},
		[searchTerm, filterProjects, clearFilter]
	);

	const featuredProjects = projects.slice(0, 2);
	const remainingProjects = projects.slice(2);

	// Sort remaining projects
	const sortedRemaining = useMemo(() => {
		if (!sort.field) return remainingProjects;

		return [...remainingProjects].sort((a, b) => {
			const aVal = getNestedValue(a, sort.field);
			const bVal = getNestedValue(b, sort.field);

			if (aVal == null && bVal == null) return 0;
			if (aVal == null) return 1;
			if (bVal == null) return -1;

			const comparison =
				typeof aVal === "number" && typeof bVal === "number"
					? aVal - bVal
					: String(aVal).localeCompare(String(bVal));

			return sort.direction === "asc" ? comparison : -comparison;
		});
	}, [remainingProjects, sort]);

	const handleSort = useCallback((field: string) => {
		setSort((prev) => ({
			field,
			direction:
				prev.field === field && prev.direction === "asc" ? "desc" : "asc",
		}));
	}, []);

	const tableHeaders = isSubcontractor
		? [
			{ label: "", field: "", sortable: false },
			{ label: "Name", field: "name", sortable: true },
			{ label: "Primary Sector", field: "sector.name", sortable: true },
			{ label: "Trade Packages Value (AUD)", field: "trade_value", sortable: true },
			{ label: "Year Complete", field: "year_completed", sortable: true },
			{ label: "Trade Package/s", field: "trades[0].name", sortable: true },
		]
		: [
			{ label: "", field: "", sortable: false },
			{ label: "Name", field: "name", sortable: true },
			{ label: "Primary Sector", field: "sector.name", sortable: true },
			{ label: "Construction Value (AUD)", field: "value", sortable: true },
			{ label: "Year Complete", field: "year_completed", sortable: true },
			{ label: "Procurement Model", field: "procurement_methods[0].name", sortable: true },
		];

	return (
		<div className="space-y-6">
			{/* Search / Filter bar */}
			{pastProjects.length > 0 && (
				<div className="flex flex-col gap-3 sm:flex-row sm:items-center">
					<InputGroup className="max-w-sm">
						<InputLeftElement pointerEvents="none">
							<FiSearch className="text-gray-400" />
						</InputLeftElement>
						<Input
							placeholder="Search projects"
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							onKeyDown={handleSearchKeyDown}
							onBlur={handleSearch}
							className="!rounded-lg !border-gray-200"
						/>
					</InputGroup>
					<SectorSelect
						label=""
						value={sectorId}
						onChange={handleSectorChange}
						placeholder="Filter by sector"
						byCompanyId={companyId}
						showCount
						clearAble
						height="py-2"
						className="!mb-0"
					/>
					{filteredProjects && (
						<span className="text-sm text-gray-500">
							{projects.length} result{projects.length !== 1 ? "s" : ""}
						</span>
					)}
				</div>
			)}

			{isFiltering ? (
				<div className="space-y-4">
					<SkeletonBase width="100%" height="200px" />
					<SkeletonBase width="100%" height="200px" />
				</div>
			) : projects.length === 0 ? (
				<p className="py-8 text-center text-sm text-gray-500">
					No projects found.
				</p>
			) : (
				<>
					{/* Featured project cards — mobile: full-detail inline cards */}
					<div className="flex flex-col gap-6 md:hidden">
						{featuredProjects.map((project) => (
							<MobilePrimaryProjectCard
								key={project.id}
								project={project}
								profileTypeId={profileTypeId ?? 0}
							/>
						))}
					</div>

					{/* Featured project cards — desktop: clickable cards that open modal */}
					<div className="hidden gap-6 md:grid md:grid-cols-2">
						{featuredProjects.map((project) => (
							<ProjectCard
								key={project.id}
								project={project}
								profileTypeId={profileTypeId ?? 0}
								onClick={() => setSelectedProject(project)}
							/>
						))}
					</div>

					{/* Remaining projects */}
					{sortedRemaining.length > 0 && (
						<div>
							<h3 className="mb-3 text-base font-semibold text-gray-900">
								More projects by {company?.name}
							</h3>

							{/* Mobile: card list */}
							<div className="flex flex-col gap-3 md:hidden">
								{sortedRemaining.map((project) => (
									<MobileProjectRow
										key={project.id}
										project={project}
										profileTypeId={profileTypeId ?? 0}
										onClick={() => setSelectedProject(project)}
									/>
								))}
							</div>

							{/* Desktop: table */}
							<div className="hidden md:block">
								<div className="overflow-x-auto">
									<table className="min-w-full text-sm">
										<thead>
											<tr className="border-b border-gray-200">
												{tableHeaders.map((header, idx) => (
													<th
														key={header.field || `col-${idx}`}
														className={`px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 ${header.sortable ? "cursor-pointer hover:text-gray-700" : "w-14"}`}
														onClick={header.sortable ? () => handleSort(header.field) : undefined}
													>
														<span className="inline-flex items-center gap-1">
															{header.label}
															{header.sortable && sort.field === header.field && (
																<span>
																	{sort.direction === "asc"
																		? "▲"
																		: "▼"}
																</span>
															)}
														</span>
													</th>
												))}
											</tr>
										</thead>
										<tbody>
											{sortedRemaining.map((project) => (
												<tr
													key={project.id}
													className="cursor-pointer border-b border-gray-100 transition-colors hover:bg-blue-50"
													onClick={() => setSelectedProject(project)}
												>
													<td className="w-32 px-3 py-1.5">
														{project.images?.[0] ? (
															<FallbackImage alt={project.name} src={project.images[0]} className="max-h-32 w-32 rounded object-contain" />
														) : (
															<div className="flex max-h-32 w-32 items-center justify-center rounded bg-gray-100">
																<FiImage className="text-gray-300" size={16} />
															</div>
														)}
													</td>
													<td className="px-3 py-1.5 font-medium text-gray-900">
														{project.name}
													</td>
													<td className="px-3 py-1.5 text-gray-600">
														{project.sectors?.[0]?.name ?? "—"}
													</td>
													<td className="px-3 py-1.5 text-gray-600">
														{isSubcontractor
															? project.trade_value || "—"
															: project.value_humanized || "—"}
													</td>
													<td className="px-3 py-1.5 text-gray-600">
														{project.year_completed || "—"}
													</td>
													<td className="px-3 py-1.5 text-gray-600">
														{isSubcontractor
															? project.trades
																?.map(
																	(t: { name: string }) => t.name
																)
																.join(", ") ?? "—"
															: project.procurement_methods
																?.map((p) => p.name)
																.join(", ") ?? "—"}
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							</div>
						</div>
					)}
				</>
			)}

			{/* Project Detail Modal */}
			<ProjectDetailModal
				project={selectedProject}
				profileTypeId={profileTypeId ?? 0}
				isOpen={selectedProject !== null}
				onClose={() => setSelectedProject(null)}
			/>
		</div>
	);
};

/** Safely access nested object properties using dot/bracket notation */
function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
	return path
		.replace(/\[(\d+)\]/g, ".$1")
		.split(".")
		.reduce<unknown>(
			(acc, key) =>
				acc != null ? (acc as Record<string, unknown>)[key] : undefined,
			obj
		);
}
